/**
 * Offline Transaction Queue
 * Queues transactions when offline and syncs when back online
 */

import { idb, STORES, restoreInventoryForFailedSale, restoreInventoryForFailedPurchase, deductInventoryForSale, addInventoryForPurchase } from './idb'
import { hapticFeedback } from './haptics'

export type TransactionType = 'sale' | 'purchase' | 'payment' | 'inventory_adjust'
export type TransactionStatus = 'pending' | 'syncing' | 'synced' | 'failed'

export interface PendingTransaction {
  id?: number
  type: TransactionType
  endpoint: string
  method: 'POST' | 'PUT' | 'DELETE'
  body: unknown
  created_at: string
  status: TransactionStatus
  error?: string
  retries: number
  local_id: string // For tracking locally created items
  idempotency_key: string // Unique key for server-side deduplication
  // Owner stamps — used to guard against queue replay under a different
  // user/tenant if the same device is used by multiple people without
  // an explicit logout.
  owner_user_id?: string
  owner_tenant_id?: string
  // Set the FIRST time this transaction's cached-inventory effect is
  // reversed after a permanent failure. Guards against re-restoring the
  // same stock on every sync cycle (which inflated the van view a little
  // more each reconnect until the row was discarded).
  inventory_restored?: boolean
}

// Event emitter for sync status updates
type SyncEventHandler = (event: { type: string; data?: unknown }) => void
const syncListeners: Set<SyncEventHandler> = new Set()

export function onSyncEvent(handler: SyncEventHandler): () => void {
  syncListeners.add(handler)
  return () => syncListeners.delete(handler)
}

function emitSyncEvent(type: string, data?: unknown) {
  syncListeners.forEach(handler => handler({ type, data }))
}

/**
 * Add a transaction to the offline queue
 */
export async function queueTransaction(
  type: TransactionType,
  endpoint: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body: unknown,
  /**
   * Optional key the caller already minted. Use when an online POST
   * attempt failed and the same key must be reused on the retry —
   * otherwise the server can't deduplicate against the original try
   * (which may have succeeded server-side before the network dropped).
   */
  idempotencyKey?: string
): Promise<PendingTransaction> {
  // Stamp the owner so a later sync (potentially under a different
  // session on the same device) can refuse to replay another user's
  // writes. localStorage is the source of truth at queue time — the
  // AuthContext sets it at login and clears it at logout.
  const ownerUserId = (typeof localStorage !== 'undefined' ? localStorage.getItem('user_id') : null) || undefined
  const ownerTenantId = (typeof localStorage !== 'undefined' ? localStorage.getItem('tenant_id') : null) || undefined

  const transaction: PendingTransaction = {
    type,
    endpoint,
    method,
    body,
    created_at: new Date().toISOString(),
    status: 'pending',
    retries: 0,
    local_id: `local_${crypto.randomUUID()}`,
    idempotency_key: idempotencyKey || crypto.randomUUID(),
    owner_user_id: ownerUserId,
    owner_tenant_id: ownerTenantId,
  }

  await idb.put(STORES.PENDING_TRANSACTIONS, transaction)
  emitSyncEvent('queue_updated')

  return transaction
}

/**
 * Transactions the sync loop should attempt. 'failed' is TERMINAL — those
 * rows only leave via the failed-queue UI (Retry resets them to pending,
 * Discard removes them). Including failed rows here caused permanently-
 * failing transactions to re-sync (and re-reverse inventory) every cycle.
 */
export async function getPendingTransactions(): Promise<PendingTransaction[]> {
  const all = await idb.getAll<PendingTransaction>(STORES.PENDING_TRANSACTIONS)
  return all.filter(tx => tx.status === 'pending')
}

/**
 * Count of ALL unsynced work (pending + failed) — used for the logout
 * warning, where a failed-but-retryable sale is still unsynced work.
 */
export async function getPendingCount(): Promise<number> {
  const all = await idb.getAll<PendingTransaction>(STORES.PENDING_TRANSACTIONS)
  return all.filter(tx => tx.status === 'pending' || tx.status === 'failed').length
}

/** Permanently-failed transactions (terminal until the user retries/discards). */
export async function getFailedTransactions(): Promise<PendingTransaction[]> {
  const all = await idb.getAll<PendingTransaction>(STORES.PENDING_TRANSACTIONS)
  return all.filter(tx => tx.status === 'failed')
}

/** Discard a permanently-failed transaction (user acknowledged the error). */
export async function discardFailedTransaction(id: number): Promise<void> {
  await idb.remove(STORES.PENDING_TRANSACTIONS, id)
  emitSyncEvent('queue_updated')
}

/** Reset a failed transaction so the next sync picks it up again. */
export async function retryFailedTransaction(id: number): Promise<void> {
  const tx = await idb.get<PendingTransaction>(STORES.PENDING_TRANSACTIONS, id)
  if (!tx) return
  // The failure handler reversed this transaction's cached-inventory
  // effect. Re-apply it before retrying, otherwise a successful retry
  // leaves the cache credited AND the server deducted (double count).
  if (tx.inventory_restored) {
    await reapplyInventoryForRetriedTransaction(tx)
    tx.inventory_restored = false
  }
  tx.status = 'pending'
  tx.retries = 0
  tx.error = undefined
  await idb.put(STORES.PENDING_TRANSACTIONS, tx)
  emitSyncEvent('queue_updated')
}

/**
 * Update a transaction status
 */
export async function updateTransactionStatus(
  id: number,
  status: TransactionStatus,
  error?: string
): Promise<void> {
  // CRITICAL: pass id as-is (number). The PENDING_TRANSACTIONS store uses
  // an autoIncrement numeric keyPath, and IDB key equality is type-strict.
  // Previously we stringified id here, so the lookup always returned
  // undefined and every "status update" silently no-op'd — retries never
  // incremented, the max-retry cap never tripped, and failing transactions
  // looped forever.
  const tx = await idb.get<PendingTransaction>(STORES.PENDING_TRANSACTIONS, id)
  if (tx) {
    tx.status = status
    if (error) tx.error = error
    if (status === 'failed') tx.retries++
    await idb.put(STORES.PENDING_TRANSACTIONS, tx)
    emitSyncEvent('queue_updated')
  }
}

/**
 * Remove a synced transaction
 */
export async function removeTransaction(id: number): Promise<void> {
  await idb.remove(STORES.PENDING_TRANSACTIONS, id)
  emitSyncEvent('queue_updated')
}

// API configuration
let apiBaseUrl = ''
let getAuthHeaders: () => HeadersInit = () => ({})

/**
 * Configure the offline queue with API settings
 */
export function configureOfflineQueue(config: {
  apiBaseUrl: string
  getHeaders: () => HeadersInit
}): void {
  apiBaseUrl = config.apiBaseUrl
  getAuthHeaders = config.getHeaders
}

/**
 * Reverse a failed transaction's cached-inventory effect EXACTLY ONCE.
 * The inventory_restored flag is persisted with the row, so subsequent
 * sync cycles (or app restarts) can't restore the same stock again.
 */
async function restoreInventoryOnce(tx: PendingTransaction): Promise<void> {
  if (tx.inventory_restored || !tx.id) return
  const data = tx.body as Record<string, unknown>
  if (data?.items) {
    const items = data.items as Array<{ product_id: string; quantity: number }>
    if (tx.type === 'sale' && data.source !== 'warehouse') {
      // Nothing was deducted from the van cache for a warehouse sale, so
      // there is nothing to put back.
      await restoreInventoryForFailedSale(items)
    } else if (tx.type === 'purchase' && data.destination !== 'warehouse') {
      // Nothing was added to the van cache for a warehouse purchase, so
      // there is nothing to take back — removing it would leave the van
      // short by the amount of a delivery it never received.
      await restoreInventoryForFailedPurchase(items)
    }
  }
  const fresh = await idb.get<PendingTransaction>(STORES.PENDING_TRANSACTIONS, tx.id)
  if (fresh) {
    fresh.inventory_restored = true
    await idb.put(STORES.PENDING_TRANSACTIONS, fresh)
  }
  tx.inventory_restored = true
}

/**
 * Re-apply a transaction's inventory effect when the user retries it
 * after a failure (inverse of restoreInventoryOnce). Sales deduct again;
 * purchases add again. Server remains the source of truth — the cache
 * just needs to track the same direction.
 */
async function reapplyInventoryForRetriedTransaction(tx: PendingTransaction): Promise<void> {
  const data = tx.body as Record<string, unknown>
  if (!data?.items) return
  const items = data.items as Array<{ product_id: string; quantity: number; conversion_factor?: number }>
  if (tx.type === 'sale' && data.source !== 'warehouse') {
    await deductInventoryForSale(items)
  } else if (tx.type === 'purchase' && data.destination !== 'warehouse') {
    // Warehouse purchases never touched this van's cache — see
    // restoreInventoryOnce.
    // No pre-multiplication: addInventoryForPurchase now converts to base
    // units itself, exactly as the sale path always did. Doing it here too
    // would square the factor.
    await addInventoryForPurchase(items)
  }
}

/**
 * Sync a single transaction.
 * Returns 'synced' | 'failed' | 'retry' to indicate the outcome.
 */
async function syncTransaction(tx: PendingTransaction): Promise<'synced' | 'failed' | 'retry' | 'auth_expired'> {
  if (!tx.id) return 'failed'

  try {
    await updateTransactionStatus(tx.id, 'syncing')

    const response = await fetch(`${apiBaseUrl}${tx.endpoint}`, {
      method: tx.method,
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
        'Idempotency-Key': tx.idempotency_key
      },
      body: tx.body ? JSON.stringify(tx.body) : undefined
    })

    if (response.ok) {
      await updateTransactionStatus(tx.id, 'synced')
      return 'synced'
    }

    // 401 = session expired (not a data problem). Don't mark the tx
    // failed and don't restore inventory — the work is still valid;
    // the user just needs to log back in. Leave the queue intact.
    if (response.status === 401) {
      await updateTransactionStatus(tx.id, 'pending')
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:expired'))
      }
      return 'auth_expired'
    }

    // Distinguish retryable vs non-retryable errors
    if (response.status >= 400 && response.status < 500) {
      // Business/client error (400, 404, 409, etc.) — TERMINAL. The row
      // moves to 'failed' (surfaced in the failed-queue banner for the
      // user to Retry/Discard) and never re-enters the automatic loop.
      // The worker's failure envelope is { success: false, error: { code,
      // message } } — reading only `.message` here meant every queued
      // failure surfaced in the banner as a bare "Client error 409" instead
      // of the reason the user needs (e.g. which purchase a supplier
      // invoice number clashes with).
      const errorData = await response.json().catch(() => ({ message: response.statusText }))
      const reason = errorData?.error?.message || errorData?.message || `Client error ${response.status}`
      await updateTransactionStatus(tx.id, 'failed', reason)
      return 'failed'
    }

    // Server error (500, 502, 503, etc.) — retryable: back to 'pending'
    // with the retry counted, so the next cycle picks it up. (Marking it
    // 'failed' here made every transient blip look terminal and triggered
    // an inventory reversal for work that would have succeeded.)
    const errorData = await response.json().catch(() => ({ message: response.statusText }))
    await markRetry(tx.id, errorData?.error?.message || errorData?.message || `Server error ${response.status}`)
    return 'retry'
  } catch (error) {
    // The request never got an answer — dropped connection, timeout, DNS.
    // That is NOT the server rejecting the work, so it must not burn one of
    // the three lives: a driver returning from a day offline into patchy
    // coverage would otherwise have the day's sales marked terminally
    // failed by three brief blips, with their stock restored as though the
    // sales never happened. Recorded, left pending, tried again next cycle.
    const message = error instanceof Error ? error.message : 'Network error'
    await noteTransportFailure(tx.id!, message)
    return 'retry'
  }
}

/**
 * A transport failure: back to 'pending' WITHOUT spending a retry. Keeps
 * the message so the UI can say why nothing is moving, but a connection
 * that never reached the server is not evidence about the work itself.
 */
async function noteTransportFailure(id: number, error: string): Promise<void> {
  const tx = await idb.get<PendingTransaction>(STORES.PENDING_TRANSACTIONS, id)
  if (tx) {
    tx.status = 'pending'
    tx.error = error
    await idb.put(STORES.PENDING_TRANSACTIONS, tx)
    emitSyncEvent('queue_updated')
  }
}

/** Count a retryable failure: back to 'pending' with retries incremented. */
async function markRetry(id: number, error: string): Promise<void> {
  const tx = await idb.get<PendingTransaction>(STORES.PENDING_TRANSACTIONS, id)
  if (tx) {
    tx.status = 'pending'
    tx.error = error
    tx.retries++
    await idb.put(STORES.PENDING_TRANSACTIONS, tx)
    emitSyncEvent('queue_updated')
  }
}

let isSyncing = false

// Cross-tab sync coordination using BroadcastChannel
const syncChannel = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('van-sales-sync')
  : null

let otherTabSyncing = false

if (syncChannel) {
  syncChannel.onmessage = (event) => {
    if (event.data.type === 'sync-start') otherTabSyncing = true
    if (event.data.type === 'sync-end') otherTabSyncing = false
  }
}

/**
 * Sync all pending transactions
 */
export async function syncAllPending(): Promise<{ synced: number; failed: number }> {
  // Prevent concurrent sync in this tab or across tabs
  if (isSyncing || otherTabSyncing) {
    return { synced: 0, failed: 0 }
  }

  if (!navigator.onLine) {
    return { synced: 0, failed: 0 }
  }

  isSyncing = true
  syncChannel?.postMessage({ type: 'sync-start' })
  emitSyncEvent('sync_started')

  let synced = 0
  let failed = 0
  const syncedIds: number[] = []

  try {
    const pending = await getPendingTransactions()

    // Sort by created_at to maintain order
    pending.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())

    // Guard against cross-user replay. If the queue contains transactions
    // stamped with a different user/tenant than the current session, skip
    // them — the AuthContext.logout flow normally clears the queue, but if
    // someone closed the tab without logging out and a different user logs
    // in on the same device the queued writes could otherwise post under
    // the wrong identity.
    const currentUserId = localStorage.getItem('user_id') || undefined
    const currentTenantId = localStorage.getItem('tenant_id') || undefined

    for (const tx of pending) {
      // Owner-mismatch — skip silently (will surface in failed-queue UI later)
      if (
        (tx.owner_user_id && currentUserId && tx.owner_user_id !== currentUserId) ||
        (tx.owner_tenant_id && currentTenantId && tx.owner_tenant_id !== currentTenantId)
      ) {
        continue
      }

      // Too many retryable failures — promote to TERMINAL failed. The
      // status flip means it never re-enters this loop, and the
      // once-guarded restore can't inflate inventory on later cycles.
      if (tx.retries >= 3) {
        await updateTransactionStatus(tx.id!, 'failed', tx.error || 'Gave up after 3 attempts')
        await restoreInventoryOnce(tx)
        failed++
        continue
      }

      const result = await syncTransaction(tx)

      if (result === 'synced') {
        syncedIds.push(tx.id!)
        synced++
      } else if (result === 'failed') {
        // Non-retryable business error — reverse the cached inventory once
        await restoreInventoryOnce(tx)
        failed++
      } else if (result === 'auth_expired') {
        // Session is gone — stop iterating; the user needs to log in
        // again and the queue will be retried on the next sync.
        break
      }
      // 'retry' — leave as pending with incremented retry count for next sync cycle
    }

    // Remove all synced transactions at the end of the sync cycle
    for (const id of syncedIds) {
      await removeTransaction(id)
    }

    if (synced > 0) {
      hapticFeedback.success()
    }
  } finally {
    isSyncing = false
    syncChannel?.postMessage({ type: 'sync-end' })
    emitSyncEvent('sync_completed', { synced, failed })
  }

  return { synced, failed }
}

/**
 * Check if currently syncing
 */
export function getIsSyncing(): boolean {
  return isSyncing
}

// Auto-sync when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    emitSyncEvent('online')
    // Small delay to ensure network is stable
    setTimeout(() => syncAllPending(), 1000)
  })

  window.addEventListener('offline', () => {
    emitSyncEvent('offline')
  })
}

export const offlineQueue = {
  queue: queueTransaction,
  getPending: getPendingTransactions,
  getPendingCount,
  syncAll: syncAllPending,
  configure: configureOfflineQueue,
  onSyncEvent,
  getIsSyncing,
}

export default offlineQueue
